// Quantities (plan 2B Approach, "Lots are records; quantities are integers").
// Crates, kits and units are whole counts. SCU amounts are thousandths of an SCU,
// so 2.069 SCU is 2069 and adding never drifts: the panel's
// Math.round(x*1000)/1000 on every write becomes plain integer arithmetic. A
// refinery's cSCU is a hundredth of an SCU, so one cSCU is 10 thousandths.

/** Thousandths in one SCU. */
export const MILLI = 1000;

/** Thousandths in one cSCU. */
export const CSCU = 10;

/** The largest amount a lot or prep level holds: a trillion thousandths, a billion SCU. */
export const MAX_AMOUNT = 1e12;

/** Strips the commas and spaces people type in numbers: "1,234" and "1 234" are 1234. */
function compact(text: string): string {
  return text.replace(/[\s,]/g, '');
}

/**
 * Parses an SCU amount as typed, "2.069" or "1,234.5", into thousandths. More than
 * three decimals round to the nearest thousandth, half up. Blank, negative or
 * unreadable input is null.
 */
export function parseSCU(text: string): number | null {
  const typed = compact(text);
  const match = /^(\d*)(?:\.(\d*))?$/.exec(typed);
  if (match === null || typed === '' || typed === '.') return null;
  const whole = match[1] === '' || match[1] === undefined ? 0 : Number(match[1]);
  const decimals = match[2] ?? '';
  const thousandths = Number(decimals.slice(0, 3).padEnd(3, '0'));
  const roundUp = Number(decimals.charAt(3) || '0') >= 5 ? 1 : 0;
  const amount = whole * MILLI + thousandths + roundUp;
  return Number.isSafeInteger(amount) && amount <= MAX_AMOUNT ? amount : null;
}

/** Parses a whole count as typed, "12" or "1,200". Blank, negative, fractional or unreadable input is null. */
export function parseCount(text: string): number | null {
  const typed = compact(text);
  if (!/^\d+$/.test(typed)) return null;
  const count = Number(typed);
  return Number.isSafeInteger(count) && count <= MAX_AMOUNT ? count : null;
}

/** "1,234" with en-US grouping. */
export function fmtCount(count: number): string {
  return count.toLocaleString('en-US');
}

/**
 * Thousandths as SCU, as the panel's fmtSCU shows them (line 531): grouped, with up
 * to three decimals and no trailing zeros. 2069 is "2.069", 2500 is "2.5", 1234000 is "1,234".
 */
export function fmtSCU(thousandths: number): string {
  const sign = thousandths < 0 ? '-' : '';
  const size = Math.abs(thousandths);
  const whole = Math.floor(size / MILLI);
  const fraction = String(size % MILLI)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return `${sign}${fmtCount(whole)}${fraction === '' ? '' : `.${fraction}`}`;
}

/** Whole SCU as thousandths: a crate's size, or a prep level typed in whole SCU. */
export function scu(whole: number): number {
  return whole * MILLI;
}

/** cSCU as thousandths of an SCU: 1234 cSCU is 12.34 SCU, 12340. */
export function fromCSCU(cscu: number): number {
  return cscu * CSCU;
}

/**
 * Parses the cSCU picked up for a refinery line as typed (R14): commas and spaces
 * are stripped and the result rounded to a whole cSCU. Blank is null (not picked
 * up); negative or unreadable input is an error.
 */
export function parseCSCU(text: string): { ok: true; cscu: number | null } | { ok: false } {
  const typed = compact(text);
  if (typed === '') return { ok: true, cscu: null };
  if (!/^\d*(\.\d*)?$/.test(typed) || typed === '.') return { ok: false };
  const cscu = Math.round(Number(typed));
  return Number.isSafeInteger(cscu) && cscu * CSCU <= MAX_AMOUNT ? { ok: true, cscu } : { ok: false };
}
